import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { debounce } from "lodash";

import { Grid, Button, CircularProgress, Box } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";

import themeColorsDesingSystem from "assets/theme/ds-colors";

function buildQueryString(params) {
  const parts = Object.entries(params).filter(
    ([, value]) => value != null && value !== "",
  );
  if (parts.length === 0) return "";
  return "?" + parts.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

function mapLabelOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((item) => item != null && String(item).trim() !== "")
    .map((item) => {
      const label = typeof item === "string" ? item.trim() : String(item?.Descricao ?? item).trim();
      return { id: label, label };
    });
}

function mapProductLineOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((item) => item?.Descricao?.trim())
    .map((item) => ({
      id: item.Descricao.trim(),
      label: item.Descricao.trim(),
      IdProdutoLinha: item.IdProdutoLinha,
    }));
}

export default function ModalConsultFilters({
  values,
  onChange,
  onSearch,
  searching,
  idMarca,
  category,
}) {
  const [subcategories, setSubcategories] = useState([]);
  const [commercialNames, setCommercialNames] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    subcategories: false,
    commercialNames: false,
    productLines: false,
  });

  const commercialLoadSeqRef = useRef(0);
  const idMarcaRef = useRef(idMarca);
  const categoryRef = useRef(category);
  idMarcaRef.current = idMarca;
  categoryRef.current = category;

  const categoryLabel = category?.label ?? "";

  const handleFieldChange = (key, value) => {
    onChange({ ...values, [key]: value });
  };

  const handleGetSubcategories = useCallback(async (categoria) => {
    if (!idMarcaRef.current || !categoria) {
      setSubcategories([]);
      return;
    }

    setLoadingFilters((prev) => ({ ...prev, subcategories: true }));
    try {
      const query = buildQueryString({
        categoria,
        idMarca: idMarcaRef.current,
      });
      const response = await Api.get(
        "product-subcategory",
        "product",
        query,
        true,
      );
      setSubcategories(
        mapLabelOptions(
          response?.Result?.ListaSubCategoria?.map((item) => item.Descricao) ?? [],
        ),
      );
    } catch (error) {
      console.error("Erro ao buscar subcategorias", error);
      setSubcategories([]);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, subcategories: false }));
    }
  }, []);

  const handleGetCommercialNames = useCallback(async (searchTerm = "") => {
    if (!idMarcaRef.current) {
      setCommercialNames([]);
      return;
    }

    const seq = ++commercialLoadSeqRef.current;
    setLoadingFilters((prev) => ({ ...prev, commercialNames: true }));

    try {
      const query = buildQueryString({
        idMarca: idMarcaRef.current,
        filter: searchTerm?.trim() || undefined,
      });
      const response = await Api.get(
        "product-nome-produto-edi-list",
        "product",
        query,
        true,
      );

      if (seq !== commercialLoadSeqRef.current) return;
      setCommercialNames(mapLabelOptions(response?.Result ?? []));
    } catch (error) {
      if (seq !== commercialLoadSeqRef.current) return;
      console.error("Erro ao buscar nomes comerciais", error);
      setCommercialNames([]);
    } finally {
      if (seq === commercialLoadSeqRef.current) {
        setLoadingFilters((prev) => ({ ...prev, commercialNames: false }));
      }
    }
  }, []);

  const debouncedGetCommercialNames = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetCommercialNames(searchTerm);
      }, 650),
    [handleGetCommercialNames],
  );

  const handleCommercialInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetCommercialNames(inputValue ?? "");
    },
    [debouncedGetCommercialNames],
  );

  const handleGetProductLines = useCallback(async () => {
    setLoadingFilters((prev) => ({ ...prev, productLines: true }));
    try {
      const response = await Api.get("products-line-list", "product", "", true);
      const list = Array.isArray(response?.Result)
        ? response.Result
        : response?.Result?.Linha ?? [];
      setProductLines(mapProductLineOptions(list));
    } catch (error) {
      console.error("Erro ao buscar linhas de produto", error);
      setProductLines([]);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, productLines: false }));
    }
  }, []);

  useEffect(() => {
    handleGetProductLines();
  }, [handleGetProductLines]);

  useEffect(() => {
    if (categoryLabel) {
      handleGetSubcategories(categoryLabel);
      return;
    }
    setSubcategories([]);
    if (values.subcategory) {
      onChange({ ...values, subcategory: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryLabel, idMarca, handleGetSubcategories]);

  useEffect(() => {
    return () => {
      debouncedGetCommercialNames.cancel();
    };
  }, [debouncedGetCommercialNames]);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
    >
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <InputText
            label="Pesquisar produto"
            placeholder="Buscar..."
            value={values.productSearch ?? ""}
            onChange={(e) => handleFieldChange("productSearch", e.target.value)}
            disabled={searching}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AutoCompleteField
            options={productLines}
            label="Linha"
            optionLabelKey="label"
            optionIdKey="id"
            onChange={(option) => handleFieldChange("productLine", option)}
            value={values.productLine}
            disabled={searching || loadingFilters.productLines}
            disableClientFilter
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AutoCompleteField
            options={commercialNames}
            label="Nome Comercial"
            optionLabelKey="label"
            optionIdKey="id"
            onChange={(option) => handleFieldChange("commercialName", option)}
            onInputChange={handleCommercialInputChange}
            value={values.commercialName}
            disabled={searching}
            loading={loadingFilters.commercialNames}
            preserveInputWhenLoading
            disableClientFilter
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AutoCompleteField
            options={subcategories}
            label="Subcategoria"
            optionLabelKey="label"
            optionIdKey="id"
            onChange={(option) => handleFieldChange("subcategory", option)}
            value={values.subcategory}
            disabled={
              searching ||
              loadingFilters.subcategories ||
              !categoryLabel
            }
            disableClientFilter
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={searching}
              style={{
                minWidth: 160,
                height: 40,
                fontWeight: 700,
                backgroundColor: themeColorsDesingSystem.primary.main,
              }}
            >
              {searching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Consultar"
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
